/**
 * The zod-free half of `logo.ts`: the `Logo` shape as a TypeScript type, the
 * rendition-size constants, and `faviconEntries`, which turns a stored logo
 * into the `<link rel="icon">` set a page head renders.
 *
 * Split out so the CLIENT can import it. `logo.ts` builds its zod schemas at
 * module top level (`z.object(...)`), which Rollup cannot tree-shake, so a
 * `head()` that imported `faviconEntries` from there pulled the whole zod
 * runtime into the client entry chunk — and the blog routes' `head()`s live
 * in that chunk, so every landing-page visitor on mobile paid for it before
 * first paint (landing mobile PageSpeed). `logo.ts` re-exports everything
 * here, so server code keeps its single import. Guarded by
 * `src/__tests__/landing-critical-path.test.ts`.
 */

// Describes the uploaded source (#1100/US3): a raster's actual pixel
// dimensions, or an SVG's intrinsic size (from `viewBox`, else width/height).
// Distinct from `UploadBlogLogoResult.width/height`, which always report the
// rasterized "main" WebP's pixel size regardless of source kind.
export interface LogoSource {
  kind: "raster" | "svg";
  width: number;
  height: number;
}

export interface Logo {
  url: string;
  favicon32: string;
  favicon180: string;
  // PNG-in-ICO rendition (16+32+48). `renderBlogCore`'s `/favicon.ico`
  // redirect (#1098) prefers it over `favicon32` so pre-US3 rows keep working
  // unmodified.
  ico?: string;
  // Present only when the source was SVG — the original bytes, served as-is.
  svg?: string;
  favicon16?: string;
  favicon48?: string;
  icon192?: string;
  icon512?: string;
  source?: LogoSource;
}

// `<link rel="icon">` size — the standard small favicon.
export const FAVICON_SIZE = 32;
// `<link rel="apple-touch-icon">` size — iOS home-screen icon.
export const APPLE_TOUCH_ICON_SIZE = 180;

// The full stored rendition set (#1100/US3) — one size per key on `Logo`.
// `favicon32`/`favicon180` alias the two constants above rather than
// duplicating the numbers, since both predate this set and keep their names.
export const ICON_SIZES = {
  favicon16: 16,
  favicon32: FAVICON_SIZE,
  favicon48: 48,
  favicon180: APPLE_TOUCH_ICON_SIZE,
  icon192: 192,
  icon512: 512,
} as const;

export interface FaviconEntry {
  rel: "icon" | "apple-touch-icon" | "manifest";
  href: string;
  sizes?: string;
  type?: string;
}

// The custom-domain web manifest's path (#1101, spec 1059-blog-icons US4) —
// served by `renderBlogCore`'s literal-route match (`webManifest.ts`), never
// by a `storeBlogImage` upload. A RELATIVE, same-origin path, unlike every
// other `FaviconEntry.href` here: both consumers must treat a `rel:"manifest"`
// entry's href as exempt from the https-absolute-URL re-check they apply to
// the others (`blogHead.ts`'s `httpsImageUrl` filter; `seo.ts` drops the
// entry outright per A8, so its own `coverImageSrc` re-check never sees it).
export const MANIFEST_PATH = "/manifest.webmanifest";

/**
 * The favicon `<link>` entries a publication logo produces, in emission order —
 * ONE list serialized by both renderers of the same data: the custom-domain
 * HTML head (`blogHead.ts`) and the hosted `/blog` route head (`src/lib/blog/
 * seo.ts`). Neither may spell the rule itself; an icon size or a new rendition
 * changes here and both heads follow. Empty when there is no logo, so callers
 * spread it unconditionally. Dependency-free on purpose: the client entry
 * chunk value-imports this module (`src/lib/blog/seo.ts`), so nothing here
 * may pull in zod, sharp or firebase-functions.
 *
 * A full US3 logo (#1100) emits, in order: the ICO rendition (falling back to
 * the legacy `favicon32` PNG when no `ico` was ever generated — a pre-US3 row,
 * or a US3 upload whose source produced no `ico` yet, cannot happen in
 * practice but the fallback keeps every row resolving), the SVG rendition
 * when present, the apple-touch icon (always — every row since #841 carries
 * `favicon180`), and a `manifest` link only when BOTH `icon192` AND
 * `icon512` are present (a half-populated pair can't build a valid manifest,
 * see `webManifest.ts`). A legacy logo (`url`/`favicon32`/`favicon180` only)
 * therefore produces exactly today's two entries — byte-identical, no
 * `manifest` — since none of the newer keys exist to add one.
 */
export function faviconEntries(logo: Logo | null | undefined): FaviconEntry[] {
  if (!logo) return [];
  const entries: FaviconEntry[] = [
    logo.ico
      ? { rel: "icon", href: logo.ico, sizes: "48x48 32x32 16x16" }
      : {
          rel: "icon",
          href: logo.favicon32,
          sizes: `${FAVICON_SIZE}x${FAVICON_SIZE}`,
          type: "image/png",
        },
  ];
  if (logo.svg) {
    entries.push({
      rel: "icon",
      href: logo.svg,
      type: "image/svg+xml",
      sizes: "any",
    });
  }
  entries.push({
    rel: "apple-touch-icon",
    href: logo.favicon180,
    // `sizes` on every icon-shaped entry, from the same constants the
    // renditions are generated at — asymmetry here reads as "the apple icon
    // has no known size" rather than as the shorthand it would be. No `type`:
    // unlike `rel="icon"`, a `rel="apple-touch-icon"` is PNG by definition.
    sizes: `${APPLE_TOUCH_ICON_SIZE}x${APPLE_TOUCH_ICON_SIZE}`,
  });
  if (logo.icon192 && logo.icon512) {
    entries.push({ rel: "manifest", href: MANIFEST_PATH });
  }
  return entries;
}
